// NOTE: Previously had a global react-hooks/exhaustive-deps disable. Removed to allow lint to catch missing dependencies.

import React, { useEffect, useMemo, useState } from 'react';
import './ActionBar.css';

import CdSelect from '../Select/Select';
import { Input } from '../Input/Input';

interface ActionBarProps {
  currentSendingDomain: string;
  fromAddress: string;
  sendingDomains: string[];
  remainingCredits: number;
  updatedOn: string | undefined;
  handleRunPreview: (sendingDomain: string, fromAddress: string) => void;
  disabled?: boolean;
}

export function isValidEmail(email: string) {
  const emailRegex =
    /^(?!.*\.\.)^[^.][a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+(?<!\.)@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+(?:[A-Za-z]{2,}|(?:\d{1,3}\.){1,3}\d{1,3}|localhost)$/;
  return emailRegex.test(email);
}

export const ActionBar = ({
  currentSendingDomain,
  fromAddress,
  sendingDomains,
  remainingCredits,
  updatedOn,
  handleRunPreview,
  disabled,
}: ActionBarProps) => {
  enum SendingDomainState {
    HasSendingDomains,
    IsSingleSendingDomain,
    NoSendingDomain,
  }
  const [selectedSendingDomain, setSelectedSendingDomain] = useState('');
  const [updatedFromAddress, setUpdatedFromAddress] = useState(fromAddress);
  const [valid, setValid] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');

  const sendingDomainStatus = useMemo(() => {
    if (sendingDomains.length > 1) {
      return SendingDomainState.HasSendingDomains;
    } else if (sendingDomains.length === 1) {
      return SendingDomainState.IsSingleSendingDomain;
    } else {
      return SendingDomainState.NoSendingDomain;
    }
  }, [sendingDomains]);

  useEffect(() => {
    if (currentSendingDomain) {
      setSelectedSendingDomain(currentSendingDomain);
    }

    if (sendingDomainStatus === SendingDomainState.IsSingleSendingDomain) {
      setSelectedSendingDomain(sendingDomains[0] ?? '');
    }
  }, [currentSendingDomain, sendingDomainStatus]);

  useEffect(() => {
    const updateLastUpdated = () => {
      if (updatedOn) {
        const date = new Date(updatedOn);
        const options: Intl.DateTimeFormatOptions = {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        };
        const humanReadableDate = date.toLocaleString('en-US', options);
        setLastUpdated(humanReadableDate);
      } else {
        setLastUpdated('N/A');
      }
    };
    updateLastUpdated();
  }, [updatedOn]);

  useEffect(() => {
    if (remainingCredits <= 0 || disabled) {
      setValid(false);
    } else {
      setValid(false); // always start with false and check for valid conditions
      if (sendingDomainStatus === SendingDomainState.NoSendingDomain && updatedFromAddress !== '') {
        if (isValidEmail(updatedFromAddress)) {
          setValid(true);
          setEmailError('');
        } else {
          setValid(false);
          setEmailError('Enter a valid email address.');
        }
      } else if (
        sendingDomainStatus === SendingDomainState.HasSendingDomains &&
        selectedSendingDomain === ''
      ) {
        setValid(false);
      } else if (
        (selectedSendingDomain !== '' && sendingDomains.length > 0) ||
        updatedFromAddress !== ''
      ) {
        setValid(true);
      }
    }
  }, [remainingCredits, sendingDomainStatus, selectedSendingDomain, updatedFromAddress, disabled]);

  const handleUpdatedFromAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedFromAddress(event.target.value);
  };

  const handleSendingDomainChange = (value: string) => {
    setSelectedSendingDomain(value);
  };

  const renderSendingDomainOptions = useMemo(() => {
    switch (sendingDomainStatus) {
      case SendingDomainState.HasSendingDomains:
        return (
          <>
            <option
              value=""
              disabled
              className="font-sans text-cds-1 leading-cds-3 font-semibold text-cds-on-surface-2 font-feature-off"
            >
              Select an option
            </option>
            {sendingDomains.map((domain) => (
              <option
                key={domain}
                value={domain}
              >
                {domain}
              </option>
            ))}
          </>
        );
      case SendingDomainState.IsSingleSendingDomain:
        return (
          <option
            value={sendingDomains[0]}
            disabled
          >
            {sendingDomains[0]}
          </option>
        );
      default:
        return null;
    }
  }, [sendingDomainStatus, sendingDomains]);

  return (
    <div
      data-testid="action-bar"
      className="toolbar flex justify-between md:items-center sm:gap-2 px-4 py-2"
    >
      <p className="flex-grow font-sans text-cds-3 leading-cds-3 font-normal text-cds-on-surface-2">
        Showing results from latest preview: {lastUpdated || 'N/A'}
      </p>

      <div className="flex justify-between flex-grow">
        {sendingDomainStatus !== SendingDomainState.NoSendingDomain ? (
          <div className="flex flex-col gap-1 justify-center">
            <label className="mb-1 actionbar-sending-label">Sending From</label>
            <div className="relative inline-block">
              <CdSelect
                disabled={disabled}
                preset={selectedSendingDomain}
                onChange={handleSendingDomainChange}
              >
                {renderSendingDomainOptions}
              </CdSelect>
            </div>
          </div>
        ) : (
          sendingDomainStatus === SendingDomainState.NoSendingDomain && (
            <div className="flex flex-col gap-1 justify-center">
              <label className="mb-1 actionbar-sending-label">Sending From Email</label>
              <div className="relative inline-block">
                <Input
                  disabled={disabled}
                  value={updatedFromAddress}
                  onValueChange={handleUpdatedFromAddress}
                  error={emailError}
                />
              </div>
            </div>
          )
        )}
      </div>

      <button
        className={`actionbar-run-btn font-sans text-cds-1 leading-cds-3 text-center tracking-normal bg-click-purple-40 hover:bg-violet-50 active:bg-violet-30 focus:border-click-purple-70 focus:bg-violet-50 text-cds-on-surface-inverse-1 disabled:bg-cds-disabled-container-2 disabled:opacity-50 disabled:cursor-not-allowed ${!valid ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={!valid}
        onClick={() => handleRunPreview(selectedSendingDomain ?? '', updatedFromAddress)}
      >
        Run New Inbox Preview
      </button>
    </div>
  );
};
